import { ButtonHTMLAttributes, cloneElement, MouseEvent, useState } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'

export type CtaButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onMouseEnter' | 'onMouseLeave'
> & {
  href?: string
  target?: string
  variant?: 'primary' | 'secondary' | 'tertiary' | 'link'
  typography?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'large'
    | 'regular'
    | 'small'
    | 'caption'
    | 'overline'
  endIcon?: React.ReactElement | null
  iconSize?: string
  loading?: boolean
  // eslint-disable-next-line no-unused-vars
  onMouseEnter?: (e: MouseEvent<HTMLElement>) => void
  // eslint-disable-next-line no-unused-vars
  onMouseLeave?: (e: MouseEvent<HTMLElement>) => void
}

const CtaButton = ({
  href,
  target,
  children,
  variant = 'link',
  endIcon = <SVGIcon symbol="cta-arrow" aria-hidden="true" />,
  iconSize = '16px',
  typography = 'regular',
  className,
  loading,
  onMouseEnter,
  onMouseLeave,
  ...props
}: CtaButtonProps) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleMouseEnter = (e: MouseEvent<HTMLElement>) => {
    setIsHovered(true)
    onMouseEnter?.(e)
  }

  const handleMouseLeave = (e: MouseEvent<HTMLElement>) => {
    setIsHovered(false)
    onMouseLeave?.(e)
  }

  const isCtaArrow = endIcon?.props?.symbol === 'cta-arrow'
  const sizedEndIcon = loading ? (
    <Spinner variant="small" />
  ) : endIcon ? (
    cloneElement(endIcon, {
      style: {
        ...endIcon.props.style,
        width: iconSize,
        height: iconSize
      }
    })
  ) : (
    endIcon
  )
  const rotatedEndIcon =
    sizedEndIcon && isCtaArrow
      ? cloneElement(sizedEndIcon, {
          className: cn(
            sizedEndIcon.props.className,
            !loading ? 'transition-transform duration-300 ease-out-cubic' : '',
            isHovered && 'rotate-45'
          )
        })
      : sizedEndIcon

  const alignClass = '[&>span]:translate-y-[.12em] [&>svg]:translate-y-[.1em]'

  if (href) {
    return (
      <Button
        asChild
        variant={variant}
        {...props}
        className={cn('group justify-between', alignClass, className)}
        endIcon={rotatedEndIcon}
      >
        <Link
          href={href}
          target={target}
          scroll={false}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn('flex')}
        >
          <Typography
            className="transition-opacity duration-300 group-hover:opacity-50"
            variant={typography}
          >
            {children}
          </Typography>
        </Link>
      </Button>
    )
  }

  return (
    <Button
      variant={variant}
      {...props}
      className={cn(
        'group justify-between *:text-trim disabled:pointer-events-none disabled:opacity-40',
        alignClass,
        className
      )}
      endIcon={rotatedEndIcon}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children ? (
        <Typography
          className="transition-opacity duration-300 group-hover:opacity-50"
          variant={typography}
        >
          {children}
        </Typography>
      ) : null}
    </Button>
  )
}

export default CtaButton
